import { CircularProgress, Button, Box, Drawer, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Questionnaire from '../../../models/questionnaire'
import { fetchStartupDetails, getdownloadLink, getQuestionnaire, submitQuestionnaire } from '../../../networking/network'
import { useAppSelector } from '../../../store/hooks'
import OverallComments from '../questionnaire/OverallComments'
import OverallConfidenceQuestion, { ConfidenceInterval } from '../questionnaire/OverallConfidenceQuestion'
import PitchDeckQualityQuestion, { PitchDeckQuality } from '../questionnaire/PitchDeckQualityQuestion'
import QuestionnaireSection from '../questionnaire/QuestionnaireSection'
import { styled } from '@mui/material/styles'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'

const drawerWidth = '40vw'

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: drawerWidth
  })
}))

interface Props {

}

const CommercialViabilityForm = (props: Props) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null)
  const [questionnaireLoading, setQuestionnaireLoading] = useState(true)

  const [readyToSubmit, setReadyToSubmit] = useState(false)

  const [open, setOpen] = useState(true)
  const [deckLoading, setDeckLoading] = useState(true)
  const [deckDownloadLink, setDeckDownloadLink] = useState<string | undefined>(undefined)

  const accessToken = useAppSelector(state => state.user.token)

  useEffect(() => {
    if (accessToken === null) {
      return
    }
    const fetchQuestionnaire = async () => {
      const questionnaire: any = await getQuestionnaire(accessToken)
      setQuestionnaireLoading(false)
      setQuestionnaire(questionnaire)
    }
    fetchQuestionnaire()
  }, [])

  const { id } = useParams()

  useEffect(() => {
    if (accessToken === null || id === undefined) {
      return
    }

    const fetchDeckURL = async () => {
      setDeckLoading(true)
      const startup = await fetchStartupDetails(accessToken, id)
      const deckUrl = startup.deckUrl
      if (!deckUrl) {
        setOpen(false)
        setDeckLoading(false)
      } else {
        const downloadLink = await getdownloadLink(accessToken, deckUrl)
        setDeckDownloadLink(downloadLink)
        setDeckLoading(false)
      }
    }
    
    fetchDeckURL()
  }, [])

  const navigation = useNavigate()

  if (accessToken === null) {
    return (
      <React.Fragment></React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Main open={open}>
        <Box 
          display='flex'
          justifyContent='flex-end'
          sx={{
            width: '100%',
            position: '-webkit-sticky; sticky',
            top: 0
          }}
        >
          <Button
            variant='contained'
            sx={{
              width: '44px',
              maxWidth: '44px',
              height: '44px',
              borderRadius: '22px',
              margin: '12px',
              zIndex: 100,
              padding: 0
            }} 
            onClick={() => {
              setOpen(!open)
            }}
          >
            {open &&
              <ArrowForwardIos sx={{
                margin: 0
              }} />
            }
            {!open &&
              <ArrowBackIos sx={{
                margin: 0
              }} />
            }
          </Button>
        </Box>
        {questionnaireLoading &&
          <>
            <CircularProgress />
          </>
        }
        {questionnaire &&
          <Box sx={{
            width: '100%'
          }}>
            {questionnaire.sections.map((section) => (
              <QuestionnaireSection 
                key={section.name} 
                section={section}
                onAnswerSelection={(question, answer, comment) => {
                  questionnaire.selectAnswer(answer, question, comment)
                  setReadyToSubmit(questionnaire.isCompleted())
                }}
              />
            ))
            }
            <OverallConfidenceQuestion onAnswerSelection={(confidence: ConfidenceInterval) => {
              questionnaire.overallConfidence = confidence
              setReadyToSubmit(questionnaire.isCompleted())
            }} />
            <OverallComments onCommentsChange={(comments: string) => {
              questionnaire.overallComments = comments
              setReadyToSubmit(questionnaire.isCompleted())
            }} />
            <PitchDeckQualityQuestion onAnswerSelection={(quality: PitchDeckQuality) => {
              questionnaire.pitchDeckQuality = quality
              setReadyToSubmit(questionnaire.isCompleted())
            }} />
            <Box sx={{
              width: '100%',
              display: 'flex',
              padding: '12px 12px 36px 12px'
            }} alignContent='center' justifyContent='center'>
              <Button
                variant='contained'
                disabled={!readyToSubmit}
                color='info'
                onClick={() => {
                  const submit = async () => {
                    await submitQuestionnaire(accessToken, id!, questionnaire)
                    navigation(-1)
                  }
                  submit()
                }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        }
      </Main>
      <Drawer sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box'
        }
      }}
      variant="persistent"
      anchor="right"
      open={open}>
        {deckLoading &&
          <Box 
            display='flex' 
            flexDirection='column' 
            justifyContent='space-around' 
            alignContent='center' 
            sx={{ 
              m: 2,
              width: '100%'
            }}>
            <Typography variant='body1' sx={{
              m: 2
            }}>Loading Deck...</Typography>
            <CircularProgress />
          </Box>
        }
        {deckDownloadLink !== undefined &&
          <object data={deckDownloadLink} type="application/pdf" style={{
            width: '100%',
            height: '100%'
          }} />
        }
        {!deckLoading && !deckDownloadLink &&
          <Typography variant='body1' sx={{
            fontStyle: 'italic',
            m: 2
          }}>
            No pitch deck has been uploaded for this startup.
          </Typography>
        }
      </Drawer>
    </React.Fragment>
  )
}

export default CommercialViabilityForm
